import { inject, injectable, optional } from 'inversify'
import { makeObservable, computed, action, observable } from 'mobx'
import { RouterRepository, type LastRoute } from './router.repository'
import { UserModel } from '@core/infra/authentication/user.model'
import { AdminModeRepository } from '@core/infra/authentication/admin-mode.repository'
import { Config } from '@core/config'
import type { RouteConfigEntry, RouteId } from './router.config'
import { Types } from '@core/ioc/types'
import type { LoggingGateway } from '@core/services/logging.gateway'
import type { WindowGateway } from '@core/services/window.gateway'
import type { LeaveConfirmation } from './leave-confirmation'

@injectable()
export class Router {
  @inject(RouterRepository)
  routerRepository!: RouterRepository

  @inject(UserModel)
  userModel!: UserModel

  @inject(AdminModeRepository)
  private adminModeRepository!: AdminModeRepository

  @inject(Config)
  private config!: Config

  @inject(Types.ILoggingGateway)
  private loggingGateway!: LoggingGateway

  @inject(Types.IWindowGateway)
  private windowGateway!: WindowGateway

  /**
   * Asked before an app-initiated navigation. Optional because an app need not
   * have anything to lose, and @inject throws on an unbound service.
   */
  @inject(Types.ILeaveConfirmation)
  @optional()
  private leaveConfirmation?: LeaveConfirmation

  /**
   * True while the leave question is on screen. Two rapid clicks on two
   * different links must produce one question, not two stacked dialogs — and
   * DialogRepository answers an outgoing question `false` when a second one
   * replaces it, so the first navigation would silently abort.
   */
  private confirmInFlight = false

  isLoading = false

  /**
   * Captured when planTarget bounces an unauthenticated request away from a
   * secure route — the login flow consumes it to land back where the user
   * was actually headed instead of always going Home.
   *
   * `forEmail` guards against a cross-user leak: a token-refresh failure
   * re-enters the current route *after* AuthenticationRepository.logOut() has
   * already cleared every UserScopedState (AppPresenter.handleSessionExpiry),
   * stashing this deep route too late for that reset to catch it —
   * registering Router as `Types.IUserScopedState` would not help, since the
   * value it needs to clear does not exist yet at reset time. Without the
   * guard, a second person logging in on the same browser right after would
   * resume the first person's page.
   */
  redirectTarget: {
    routeId: RouteId
    params: Record<string, string>
    query: Record<string, string>
    forEmail: string | null
  } | null = null

  /**
   * Monotonic transaction counter. Each navigation captures its own value;
   * if a newer navigation starts while hooks are awaited, the stale one
   * aborts before COMMIT ("latest wins").
   */
  private navigationSequence = 0

  // Captured once in registerRoutes (before any navigation happens), not
  // re-read per call — Navigo invokes our route-match handler *before*
  // calling history.pushState (updateBrowserURL runs after processMatches),
  // so any nested navigation triggered synchronously from within the first
  // one (e.g. the auth guard's redirect to Login) would otherwise see a
  // stale, not-yet-updated location and misread itself as "the very first
  // bare / launch" too — re-triggering RESUME and looping
  // Home -> Login -> Home -> Login forever.
  //
  // Captured there rather than in a field initialiser because it now comes
  // from a Gateway, and @inject fields are not populated during field
  // initialisation (§F).
  private initialLoadWasBareRoot = false
  private resumeDecided = false

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      redirectTarget: observable,
      currentRoute: computed,
      updateCurrentRoute: action,
      consumeRedirectTarget: action,
      goToId: action,
    })
  }

  // Wrapped at declaration rather than annotated above: makeObservable's
  // annotation map only accepts public members. Needed because the commit
  // half of updateCurrentRoute runs after an await, outside its action.
  private setLoading = action((isLoading: boolean) => {
    this.isLoading = isLoading
  })

  /**
   * One-shot read: returns the pending post-login destination (if any) and
   * clears it, so a stale target can't leak into a later, unrelated login.
   *
   * Read `this.userModel.email` rather than take it as a parameter: every
   * caller (login, enterAsGuest) has already called setSession by the time it
   * consumes this, and guest's session email is server-generated, not the
   * form's typed (usually blank) email — a parameter would make that one path
   * mismatch by construction. A target stashed `forEmail` someone else (the
   * session-expiry case — see the class comment) is discarded rather than
   * honored: still one-shot, but this login does not resume it, since doing
   * so would land a different person on the previous session's page.
   */
  consumeRedirectTarget = () => {
    const target = this.redirectTarget
    this.redirectTarget = null
    const signedInEmail = this.userModel.email
    if (target === null || (target.forEmail !== null && target.forEmail !== signedInEmail)) return null
    return { routeId: target.routeId, params: { ...target.params }, query: { ...target.query } }
  }

  /**
   * The route to resume into, or null for "carry on as requested". Non-null
   * only for the very first URL resolve of a fresh page load, and only when
   * that first URL was a bare '/' with nothing else in it — a deep link or
   * bookmark to any other URL (including '/' with a query string) is always
   * honored as typed, never overridden. Decided once and cached (see
   * initialLoadWasBareRoot/resumeDecided) so a cascade of nested navigations
   * (e.g. RESUME itself landing on a secure route that the auth guard then
   * redirects again) can never re-trigger this a second time.
   */
  private routeToResume(newRouteId: RouteId): LastRoute | null {
    if (this.resumeDecided) return null
    this.resumeDecided = true

    if (!this.initialLoadWasBareRoot) return null

    const last = this.routerRepository.readLastRoute()
    return last !== null && last.routeId !== newRouteId ? last : null
  }

  get currentRoute() {
    return this.routerRepository.currentRoute
  }

  /**
   * PLAN — pure routing policy: (requested route, session) → final destination.
   * The only rerouting mechanism in the system.
   */
  /**
   * Where a capability/admin/demo miss lands. Home for an ordinary session; a
   * share-link outsider cannot read Home (every section 403s for them), so
   * they go to their own "shared with me" landing instead (plan slice 12, S3).
   */
  private get fallbackRoute(): RouteConfigEntry {
    return this.routerRepository.findRoute(this.userModel.isExternal ? 'Shared' : 'Home')!
  }

  private planTarget = (
    route: RouteConfigEntry,
    params: Record<string, string>,
    query: Record<string, string>,
  ): RouteConfigEntry => {
    if (route.routeDef.isSecure && !this.userModel.isAuthenticated) {
      this.redirectTarget = {
        routeId: route.routeId,
        params: { ...params },
        query: { ...query },
        // '' whenever nobody was ever logged in on this browser this
        // session (the ordinary deep-link-while-signed-out case) — falls
        // back to null, which consumeRedirectTarget honors for anyone.
        forEmail: this.userModel.previousEmail || null,
      }
      return this.routerRepository.findRoute('Login')!
    }
    // Capability gate after the auth gate: an unauthenticated visitor still
    // goes to Login (and may resume here if they log in as someone
    // privileged), but an authenticated session without the capability goes
    // Home — no redirectTarget stash, since re-login wouldn't change the
    // answer.
    //
    // Display gating only: the page's own requests are refused server-side
    // regardless, so the worst a stale capability list can do is offer a page
    // that then reports Forbidden.
    if (route.routeDef.requiresCapability && !this.userModel.can(route.routeDef.requiresCapability)) {
      return this.fallbackRoute
    }
    // A share-link outsider asking for '/' directly (bookmark, RESUME) hits a
    // Home they cannot read — redirect to their landing page. Placed after the
    // capability gate so an authenticated non-external still gets Home.
    if (route.routeId === 'Home' && this.userModel.isExternal) {
      return this.routerRepository.findRoute('Shared')!
    }
    // Same display-gating caveat as the capability check above — the global
    // admin-mode switch (Phase 4, spec/30-structure.md). No
    // redirectTarget stash here either: turning the switch back on doesn't
    // reach this path, it's the toggle's own action that sends you back.
    if (route.routeDef.requiresAdminMode && !this.adminModeRepository.adminModePm) {
      return this.routerRepository.findRoute('Home')!
    }
    // C11, structural review: fixture routes (Books, the HR demo, the whole
    // clickable/expandable/columns tree) redirect the same way rather than
    // rendering in a production build — same display-gating caveat as the
    // two checks above.
    if (route.routeDef.requiresDemoSurface && !this.config.showDemoSurface) {
      return this.routerRepository.findRoute('Home')!
    }
    return route
  }

  /**
   * Response leg — called by Navigo on every URL match (and by the test pivot).
   * Runs the transaction pipeline: RESOLVE → PLAN → EXECUTE → COMMIT.
   * A navigation either commits atomically or leaves currentRoute untouched.
   *
   * Resolves `true` only once COMMIT has actually run — a RESUME/PLAN
   * redirect propagates whatever *its* eventual commit resolves to, and every
   * other exit (unknown route, failed hook, stale navigation) resolves
   * `false`. This is what makes `goToId(true)` mean the same thing for the
   * real Navigo-backed adapter as for the test pivot (spec §15.1) — and what
   * lets `goToId`'s own leave-confirmation discard only after a genuine
   * commit.
   */
  updateCurrentRoute = async (
    newRouteId: RouteId,
    params: Record<string, string> = {},
    query: Record<string, string> = {},
  ): Promise<boolean> => {
    // RESUME — bare '/' launch with a persisted last route: redirect there
    // instead, re-syncing the URL (same shape as the PLAN-step auth redirect
    // below). Any other URL is left alone.
    const resumeTarget = this.routeToResume(newRouteId)
    if (resumeTarget) {
      return this.routerRepository.goToId(resumeTarget.routeId, resumeTarget.params, resumeTarget.query)
    }

    // 1. RESOLVE
    const requestedRoute = this.routerRepository.findRoute(newRouteId)
    if (!requestedRoute) {
      this.loggingGateway.warn(`updateCurrentRoute: no route registered for id "${newRouteId}" — ignored`)
      return false
    }

    // 2. PLAN — exits before any hook, so onLeave/onEnter run at most once per transition
    const finalRoute = this.planTarget(requestedRoute, params, query)
    if (finalRoute !== requestedRoute) {
      // covers direct URL entry / back button; Navigo re-syncs the URL
      return this.routerRepository.goToId(finalRoute.routeId)
    }

    // 4. EXECUTE
    const seq = ++this.navigationSequence
    const oldRoute = this.currentRoute.routeId ? this.routerRepository.findRoute(this.currentRoute.routeId) : null

    try {
      if (oldRoute?.onLeave) await oldRoute.onLeave()
      if (finalRoute.onEnter) await finalRoute.onEnter(params)
    } catch (err) {
      this.loggingGateway.error(`Router: navigation to "${finalRoute.routeId}" aborted — hook failed`, { err })
      this.setLoading(false)
      return false
    }

    if (seq !== this.navigationSequence) return false // made stale by a newer navigation

    // 5. COMMIT
    //
    // Everything past the awaits above is outside updateCurrentRoute's own
    // `action` wrapper — MobX actions do not span an await — so the writes go
    // through explicit actions. One action for the route means observers are
    // notified once per navigation and can never read a new routeId beside the
    // previous route's params.
    this.routerRepository.setCurrentRoute(finalRoute.routeId, finalRoute.routeDef, params, query)
    this.setLoading(false)

    // Login/NotFound are never a meaningful "resume point".
    if (finalRoute.routeId !== 'Login' && finalRoute.routeId !== 'NotFound') {
      this.routerRepository.writeLastRoute({ routeId: finalRoute.routeId, params, query })
    }

    this.routerRepository.onRouteChanged?.()
    return true
  }

  /**
   * Binds this Router to the router repository and Navigo listener.
   */
  registerRoutes = (onRouteChange: () => void) => {
    // Read before handing control to the router gateway, which resolves the
    // initial URL synchronously as part of registering.
    const { pathname, search } = this.windowGateway.location()
    this.initialLoadWasBareRoot = pathname === '/' && search === ''

    this.routerRepository.registerRoutes(this.updateCurrentRoute, onRouteChange)
  }

  /**
   * Request leg — navigate to a route id. The plan is applied here too, so a
   * blocked request pushes the final URL directly (no junk history entries).
   *
   * Resolves to whether the navigation actually committed — false if the route
   * is unknown, the user declined to leave, or the transition was aborted by a
   * failed hook or superseded by a newer navigation. Callers that change state
   * *because* they navigated have to check it: doing the state change first
   * and navigating after leaves the app in a state the user never agreed to.
   *
   * Real and fake Router Gateways now agree on this: `true` means COMMIT
   * actually ran (§15.1) — awaiting it is safe in both the browser and tests.
   */
  goToId = async (
    routeId: RouteId,
    params: Record<string, string> = {},
    query: Record<string, string> = {},
  ): Promise<boolean> => {
    const route = this.routerRepository.findRoute(routeId)
    if (!route) {
      this.loggingGateway.warn(`goToId: no route registered for id "${routeId}" — ignored`)
      return false
    }

    const finalRoute = this.planTarget(route, params, query)

    // Ask before anything touches the URL. A refusal is a plain `return`:
    // the gateway is never called, so there is nothing to put back. This is
    // the only exit the app initiates and therefore the only one it can
    // genuinely prevent — see leave-confirmation.ts.
    //
    // Skipped when PLAN substituted the route: that redirect is the Router's,
    // not the user's, and answering "Stay" to a bounce off a secure route
    // would leave the user on a page they are not authorised for.
    let commitDiscard: (() => void) | null = null

    if (finalRoute === route) {
      const prepared =
        this.leaveConfirmation?.prepare({
          from: this.currentRoute.routeId,
          to: finalRoute.routeId,
        }) ?? null

      if (prepared) {
        if (this.confirmInFlight) return false

        this.confirmInFlight = true
        const decision = await prepared.confirm().finally(() => {
          this.confirmInFlight = false
        })

        if (decision.kind === 'stay') return false
        commitDiscard = decision.commitDiscard
      }
    }

    this.setLoading(true)
    const committed = await this.routerRepository.goToId(finalRoute.routeId, params, query)

    // Discard only now — the transition actually committed. A failed hook or
    // a navigation superseded by a newer one before COMMIT must lose nothing
    // (spec §15.1, §20.1).
    if (committed) commitDiscard?.()

    return committed
  }
}
